import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useMutation, useQuery } from '@apollo/client';
import gql from 'graphql-tag';
import { Link } from 'react-router-dom';
import { FormattedMessage, useIntl } from 'react-intl';
import SuccessionLayout from '../SuccessionLayout';
import { formatCurrency } from '../../../utils/formatters';
import { S3Image } from '../../../lib/s3';
import DealInterestCard from './DealInterestCard';
import DealInterestListItem from './DealInterestListItem';
import { SAVE_DEAL_INTEREST } from '../../../graphql/mutations/dealMutations';
// Error boundary component to catch Apollo errors
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    console.error("GraphQL Error caught in ErrorBoundary:", error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="bg-red-50 border border-red-200 rounded-md p-4 my-4">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 18a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2h-1V9a1 1 0 00-1-1z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-red-800">An error occurred loading this component</h3>
              <div className="mt-2 text-sm text-red-700">
                <p>Please try refreshing the page. If the problem persists, contact support.</p>
              </div>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

// GraphQL Queries
const GET_COMPANY_SETTINGS = gql`
  query GetCompanySettings {
    company {
      id
      agent_word
      agent_word_fr
      deal_room_branded_name
      deal_room_branded_name_fr
      deal_room_single_word
      deal_room_single_word_fr
      avatar_background_color
      avatar_directory
      primary_color
    }
  }
`;


const DealInvite = ({ deal, refetchDeal, activeFilter = 'all', company }) => {
  const intl = useIntl();
  const [view, setView] = useState('grid');
  const [searchTerm, setSearchTerm] = useState('');

  const [saveDealInterest] = useMutation(SAVE_DEAL_INTEREST);
  const { data: settingsData } = useQuery(GET_COMPANY_SETTINGS, {
    fetchPolicy: 'cache-first'
  });

  // Agent word configuration
  const companyData = settingsData?.company || company || {};
  const agentWord = (intl.locale === 'fr' ? (companyData.agent_word_fr || 'conseiller') : (companyData.agent_word || 'advisor')).toLowerCase();
  const agentWordLabel = agentWord.charAt(0).toUpperCase() + agentWord.slice(1);
  const agentWordPlural = agentWord.endsWith('s') ? agentWord : `${agentWord}s`;
  const agentWordPluralLabel = agentWordPlural.charAt(0).toUpperCase() + agentWordPlural.slice(1);
  const agentWordPluralLower = agentWordPlural.toLowerCase();
  // Helper function to get advisor type for badge display
  const getAdvisorBadgeInfo = (dealInterest) => {
    // First check if it's already invited (this will be the primary badge)
    if (dealInterest.workflow_state === 'invited') {
      return { text: intl.formatMessage({ id: 'succession.profile_drawer.deal_section.state_invited' }), color: 'success' };
    }
    
    // Then check workflow_state for the original type
    if (dealInterest.workflow_state === 'following') {
      return { text: 'Rolodex', color: 'primary' };
    } else if (dealInterest.workflow_state === 'follower') {
      return { text: 'Follower', color: 'accent' };
    }
    
    // Check for suggested match
    if (dealInterest.reason_for_suggestion) {
      return { text: 'Suggested Match', color: 'success' };
    }
    
    return null;
  };

  const handleSaveDealInterest = (dealInterestId) => {
    console.log("DealInvite: Saving deal interest with ID:", dealInterestId);
    
    saveDealInterest({
      variables: {
        dealInterestId: dealInterestId
      },
      update: (cache) => {
        // Update cache for immediate UI update
        const dealInterestData = cache.identify({ __typename: 'DealInterest', id: dealInterestId });
        console.log("DealInvite: dealInterestData cache ID:", dealInterestData);
        
        cache.modify({
          id: dealInterestData,
          fields: {
            saved: () => true
          }
        });
      }
    }).then((response) => {
      // Check for errors in the response
      if (response.data?.saveDealInterest?.errors?.length) {
        console.error("DealInvite: Error saving advisor:", response.data.saveDealInterest.errors);
        alert("Error saving advisor: " + response.data.saveDealInterest.errors.join(", "));
        return;
      }
      
      
      // Refetch the deal data to update counts - this is a direct callback to the parent
      if (refetchDeal && typeof refetchDeal === 'function') {
        console.log("DealInvite: Calling refetchDeal()");
        refetchDeal();
      }
    }).catch(error => {
      console.error("DealInvite: Error saving advisor:", error);
      alert("Error saving advisor. Please try again.");
    });
  };

  // Filter invited advisors based on selected filter and search term
  const filteredInvited = () => {
    if (!deal?.invited || !Array.isArray(deal.invited)) return [];
    
    let filtered = deal.invited;
    
    // Filter by saved status if needed
    if (activeFilter === 'saved') {
      filtered = filtered.filter(invited => invited.saved);
    }
    
    // Apply search term filter
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(
        (invited) => invited?.inventory_user?.user && (
          (invited.inventory_user.user.name || '').toLowerCase().includes(term) ||
          (invited.inventory_user.user.user_name || '').toLowerCase().includes(term) ||
          (invited.inventory_user.user.headline || '').toLowerCase().includes(term) ||
          (invited.inventory_user.user.city || '').toLowerCase().includes(term) ||
          (invited.inventory_user.user.province || '').toLowerCase().includes(term)
        )
      );
    }
    
    return filtered;
  };

  if (!deal) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="spinner-border animate-spin inline-block w-8 h-8 border-4 rounded-full text-succession-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  const invitedAdvisors = filteredInvited();

  return (
    <div>
      {/* Search and View Options */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-6">
        <h2 className="text-xl font-bold text-succession-text-primary mb-4 md:mb-0">
          {intl.formatMessage(
            { id: 'succession_deals.wizard.steps.deal_invite.title_with_agent_word', defaultMessage: 'Invited {agentWordPlural}' },
            { agentWordPlural: agentWordPluralLabel }
          )} ({invitedAdvisors.length})
        </h2>
        
        <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-center flex-wrap">
          <div className="relative" style={{ flex: '0 1 auto', minWidth: '180px', maxWidth: '250px' }}>
            <input
              type="text"
              placeholder={intl.formatMessage(
                {
                  id: 'succession_deals.wizard.steps.deal_invite.search_placeholder_with_agent_word',
                  defaultMessage: 'Search {agentWordPlural}...'
                },
                { agentWordPlural: agentWordPluralLower }
              )}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{
                width: '100%',
                paddingLeft: '40px',
                paddingRight: '16px',
                paddingTop: '10px',
                paddingBottom: '10px',
                borderRadius: '9999px',
                backgroundColor: 'var(--succession-bg-secondary, #f8fafc)',
                border: '1px solid var(--succession-border, #64748b)',
                color: 'var(--succession-text-primary, #0f172a)',
                fontSize: '14px',
                outline: 'none',
                transition: 'all 0.3s ease',
                boxShadow: 'none',
                WebkitAppearance: 'none',
                appearance: 'none'
              }}
              onFocus={(e) => {
                e.target.style.borderColor = 'var(--succession-primary, #5489B6)';
                e.target.style.boxShadow = '0 0 0 3px rgba(84, 137, 182, 0.1)';
              }}
              onBlur={(e) => {
                e.target.style.borderColor = 'var(--succession-border, #64748b)';
                e.target.style.boxShadow = 'none';
              }}
            />
            <div className="absolute left-3 top-3 text-succession-text-muted" style={{ pointerEvents: 'none' }}>
              <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
            </div>
          </div>
          
          <div className="flex border border-succession-border rounded-lg overflow-hidden flex-shrink-0">
            <button
              onClick={() => setView('grid')}
              className={`px-4 py-2 flex items-center ${view === 'grid' ? 'bg-succession-primary-light text-succession-primary' : 'text-succession-text-secondary'}`}
            >
              <svg className="h-5 w-5 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
              </svg>
              <FormattedMessage id="succession_deals.wizard.steps.deal_invite.view_options.grid" defaultMessage="Grid" />
            </button>
            <button
              onClick={() => setView('list')}
              className={`px-4 py-2 flex items-center ${view === 'list' ? 'bg-succession-primary-light text-succession-primary' : 'text-succession-text-secondary'}`}
            >
              <svg className="h-5 w-5 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 10h16M4 14h16M4 18h16" />
              </svg>
              <FormattedMessage id="succession_deals.wizard.steps.deal_invite.view_options.list" defaultMessage="List" />
            </button>
          </div>
        </div>
      </div>

      {/* Stage description */}
      <div className="bg-succession-primary-light border-l-4 border-succession-primary rounded-md p-4 mb-6">
        <div className="flex">
          <div className="flex-shrink-0">
            <svg className="h-5 w-5 text-succession-primary" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2h-1V9a1 1 0 00-1-1z" clipRule="evenodd" />
            </svg>
          </div>
          <div className="ml-3">
            <div className="mt-2 text-sm text-succession-primary">
              <p>
                {intl.formatMessage(
                  { id: 'succession_deals.wizard.steps.deal_invite.stage_description_with_agent_word', defaultMessage: 'Invited {agentWordPlural}: These {agentWordPluralLower} have been invited to participate in your deal.' },
                  { agentWordPlural: agentWordPluralLabel, agentWordPluralLower: agentWordPluralLower }
                )}
              </p>
              {activeFilter === 'saved' && (
                <p className="mt-1 font-medium">
                  {intl.formatMessage(
                    { id: 'succession_deals.wizard.steps.deal_invite.showing_saved_only_with_agent_word', defaultMessage: 'Showing saved {agentWordPlural} only.' },
                    { agentWordPlural: agentWordPluralLower }
                  )}
                </p>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* No invited advisors message */}
      {invitedAdvisors.length === 0 ? (
        <div className="text-center py-12">
          <svg className="mx-auto h-12 w-12 text-succession-text-muted" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <h3 className="mt-2 text-lg font-medium text-succession-text-primary">
            {activeFilter === 'saved'
              ? intl.formatMessage(
                  { id: 'succession_deals.wizard.steps.deal_invite.empty_state.no_saved_with_agent_word', defaultMessage: 'No Saved {agentWordPlural}' },
                  { agentWordPlural: agentWordPluralLabel }
                )
              : intl.formatMessage({ id: 'succession_deals.wizard.steps.deal_invite.empty_state.no_invitations', defaultMessage: 'No Invitations Yet' })}
          </h3>
          <p className="mt-1 text-sm text-succession-text-secondary">
            {activeFilter === 'saved'
              ? intl.formatMessage(
                  { id: 'succession_deals.wizard.steps.deal_invite.empty_state.no_saved_description_with_agent_word', defaultMessage: "You haven't saved any invited {agentWordPlural} yet." },
                  { agentWordPlural: agentWordPluralLower }
                )
              : intl.formatMessage(
                  { id: 'succession_deals.wizard.steps.deal_invite.empty_state.no_invitations_description_with_agent_word', defaultMessage: 'All invited {agentWordPlural} will be presented here.' },
                  { agentWordPlural: agentWordPluralLower }
                )}
          </p>
        </div>
      ) : (
        <>
          {/* Grid View */}
          {view === 'grid' && (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {invitedAdvisors.map((invited) => {
                const badgeInfo = getAdvisorBadgeInfo(invited);
                return (
                  <DealInterestCard
                    key={invited.id}
                    dealInterest={invited}
                    dealName={deal.deal_name}
                    companySettings={companyData}
                    onSave={!invited.saved ? handleSaveDealInterest : undefined}
                    viewDetailsPath={`/succession/deals/${deal.deal_name}/invite?deal_int_id=${invited.id}`}
                    badgeText={badgeInfo?.text}
                    badgeColor={badgeInfo?.color}
                  />
                );
              })}
            </div>
          )}
          
          {/* List View */}
          {view === 'list' && (
            <div className="overflow-x-auto border border-succession-border rounded-lg">
              <table className="min-w-full divide-y divide-succession-border">
                <thead className="bg-succession-bg-secondary">
                  <tr>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-succession-text-muted uppercase tracking-wider w-80">{agentWordLabel}</th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-succession-text-muted uppercase tracking-wider">Location</th>
                    <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-succession-text-muted uppercase tracking-wider">Assets</th>
                    <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-succession-text-muted uppercase tracking-wider">Revenue</th>
                    <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-succession-text-muted uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="bg-succession-bg-primary divide-y divide-succession-border">
                  {invitedAdvisors.map((invited) => {
                    const badgeInfo = getAdvisorBadgeInfo(invited);
                    return (
                      <DealInterestListItem 
                        key={invited.id}
                        dealInterest={invited}
                        dealName={deal.deal_name}
                        companySettings={companyData}
                        onSave={!invited.saved ? handleSaveDealInterest : undefined}
                        viewDetailsPath={`/succession/deals/${deal.deal_name}/invite?deal_int_id=${invited.id}`}
                        badgeText={badgeInfo?.text}
                        badgeColor={badgeInfo?.color}
                      />
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </div>
  );
};

DealInvite.propTypes = {
  deal: PropTypes.object,
  refetchDeal: PropTypes.func,
  activeFilter: PropTypes.string,
  company: PropTypes.object
};

// Wrapping component with error boundary
export default function DealInviteWithErrorBoundary(props) {
  return (
    <ErrorBoundary>
      <DealInvite {...props} />
    </ErrorBoundary>
  );
} 